import { useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowRight,
  Bot,
  Box,
  BrainCircuit,
  Cable,
  Check,
  ChevronDown,
  Clipboard,
  Clock3,
  Download,
  ExternalLink,
  FileBox,
  FileText,
  FolderOpen,
  History,
  Library,
  Menu,
  MonitorUp,
  PackageOpen,
  Paperclip,
  Plus,
  Search,
  Send,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Upload,
  WandSparkles,
  X,
  Zap,
} from 'lucide-react'
import { api } from './api.js'
import { skillShortcuts, workflows } from './workflows.js'
import ModelPreview from './components/ModelPreview.jsx'

const STATUS_LABEL = {
  solicitado: 'Solicitado',
  'em-modelagem': 'Em modelagem',
  'em-revisao': 'Em revisão',
  pronto: 'Pronto',
  arquivado: 'Arquivado',
  erro: 'Requer atenção',
}

const STATUS_OPTIONS = Object.entries(STATUS_LABEL).filter(([value]) => value !== 'erro')

const NAV_ITEMS = [
  { id: 'agent', label: 'Codex IA', icon: BrainCircuit },
  { id: 'library', label: 'Biblioteca', icon: Library },
  { id: 'requests', label: 'Solicitações', icon: FileText },
  { id: 'workflows', label: 'Fluxos rápidos', icon: Zap },
  { id: 'integrations', label: 'Integrações', icon: Cable },
  { id: 'settings', label: 'Configurações', icon: Settings },
]

const DEFAULT_SETTINGS = {
  atelierName: 'Meu Ateliê',
  defaultPrinter: 'Bambu Lab A1',
  defaultMaterial: 'PLA',
  defaultNozzle: '0,4 mm',
}

function relativeDate(iso) {
  if (!iso) return 'sem data'
  const diff = Date.now() - new Date(iso).getTime()
  const minutes = Math.max(1, Math.floor(diff / 60000))
  if (minutes < 60) return `há ${minutes} min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `há ${hours} h`
  const days = Math.floor(hours / 24)
  if (days < 30) return `há ${days} ${days === 1 ? 'dia' : 'dias'}`
  const months = Math.floor(days / 30)
  return `há ${months} ${months === 1 ? 'mês' : 'meses'}`
}

function bytes(value) {
  if (!Number.isFinite(value) || value <= 0) return '0 KB'
  const units = ['B', 'KB', 'MB', 'GB']
  let size = value
  let index = 0
  while (size >= 1024 && index < units.length - 1) {
    size /= 1024
    index += 1
  }
  return `${size.toFixed(size >= 10 || index === 0 ? 0 : 1)} ${units[index]}`
}

function inferCategory(file) {
  const extension = file.name.toLowerCase().split('.').pop()
  if (extension === 'stl') return 'stl'
  if (['step', 'stp'].includes(extension)) return 'step'
  if (extension === '3mf') return 'exports'
  if (['glb', 'gltf', 'png', 'jpg', 'jpeg', 'webp'].includes(extension)) return 'previews'
  if (['py', 'scad', 'fcstd'].includes(extension)) return 'src'
  if (['md', 'pdf', 'txt'].includes(extension)) return 'docs'
  return 'references'
}

async function uploadGrouped(projectId, files) {
  const groups = new Map()
  for (const file of files) {
    const category = inferCategory(file)
    groups.set(category, [...(groups.get(category) || []), file])
  }
  let latest
  for (const [category, group] of groups) {
    latest = await api.uploadFiles(projectId, group, category)
  }
  return latest?.project
}

function StatusDot({ status }) {
  return <span className={`status-dot status-${status}`} aria-hidden="true" />
}

function BrandMark() {
  return (
    <div className="brand-mark" aria-hidden="true">
      <span className="brand-cube"><Box size={25} /></span>
      <span>ATELIÊ 3D</span>
    </div>
  )
}

function Sidebar({ current, onChange, settings, projectCount, open, onClose }) {
  return (
    <aside className={`sidebar ${open ? 'sidebar-open' : ''}`}>
      <div className="sidebar-brand"><BrandMark /></div>
      <nav aria-label="Navegação principal">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon
          return (
            <button
              className={`nav-item ${current === item.id ? 'nav-active' : ''}`}
              key={item.id}
              onClick={() => { onChange(item.id); onClose() }}
            >
              <Icon size={21} />
              <span>{item.label}</span>
              {item.id === 'library' && projectCount > 0 && <small>{projectCount}</small>}
            </button>
          )
        })}
      </nav>
      <div className="sidebar-footer">
        <span className="local-pulse" />
        <div>
          <strong>{settings.atelierName}</strong>
          <span>Nuvem + conector Mac</span>
        </div>
      </div>
    </aside>
  )
}

function TopHeading({ title, subtitle, onNew, onMenu }) {
  return (
    <header className="page-heading">
      <button className="mobile-menu" onClick={onMenu} aria-label="Abrir menu"><Menu /></button>
      <div>
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
      </div>
      <button className="primary-action" onClick={() => onNew()}><Plus size={19} /> Novo pedido</button>
    </header>
  )
}

function ProjectThumbnail({ project }) {
  const images = project.files?.filter((file) => ['.png', '.jpg', '.jpeg', '.webp'].includes(file.extension)) || []
  const image = images.find((file) => file.relativePath.startsWith('previews/')) || images[0]
  if (image) return <img src={image.url} alt="" />
  return <Box size={28} strokeWidth={1.25} />
}

function ProjectList({ projects, selectedId, onSelect }) {
  return (
    <section className="project-list" aria-label="Modelos">
      <div className="list-heading">
        <span>Ordenar: <strong>Mais recente</strong></span>
        <span>{projects.length} {projects.length === 1 ? 'modelo' : 'modelos'}</span>
      </div>
      <div className="project-rows">
        {projects.map((project) => (
          <button
            key={project.id}
            className={`project-row ${selectedId === project.id ? 'project-row-selected' : ''}`}
            onClick={() => onSelect(project.id)}
          >
            <span className="project-thumb"><ProjectThumbnail project={project} /></span>
            <span className="project-row-copy">
              <strong>{project.title}</strong>
              <span>{project.version} · atualizado {relativeDate(project.updatedAt)}</span>
              <small><StatusDot status={project.status} /> {STATUS_LABEL[project.status] || project.status}</small>
            </span>
          </button>
        ))}
      </div>
    </section>
  )
}

function EmptyLibrary({ onNew }) {
  return (
    <section className="empty-library">
      <div className="empty-library-visual">
        <div className="layer layer-1" />
        <div className="layer layer-2" />
        <div className="layer layer-3" />
        <PackageOpen size={64} strokeWidth={1.05} />
      </div>
      <div>
        <h2>Sua biblioteca começa com um pedido.</h2>
        <p>Descreva a peça, anexe referências e o Ateliê criará uma pasta organizada para modelagem, versões e arquivos de impressão.</p>
        <button className="primary-action" onClick={() => onNew()}><Plus size={18} /> Criar primeiro pedido</button>
      </div>
    </section>
  )
}

function ProjectWorkspace({ project, onCustomize, onOpen, onBambu, onUpload, onStatusChange, busy }) {
  const stl = project.files?.find((file) => file.extension === '.stl')
  const profile = project.printProfile || {}
  const totalSize = (project.files || []).reduce((sum, file) => sum + file.size, 0)
  const uploadRef = useRef(null)
  return (
    <section className="workspace-panel">
      <div className="workspace-title">
        <div>
          <h2>{project.title}</h2>
          <p>
            <StatusDot status={project.status} />
            {STATUS_LABEL[project.status]} <span>·</span> {project.version} <span>·</span> Atualizado {relativeDate(project.updatedAt)}
          </p>
        </div>
        <label className="status-select-label">
          <span className="sr-only">Alterar status</span>
          <select value={project.status} onChange={(event) => onStatusChange(event.target.value)} disabled={busy}>
            {STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <ChevronDown size={14} />
        </label>
      </div>
      <div className="model-canvas"><ModelPreview project={project} /></div>
      <div className="model-facts">
        <div><span>Material</span><strong>{project.material || 'A definir'}</strong></div>
        <div><span>Altura de camada</span><strong>{profile.layerHeight || 'A definir'}</strong></div>
        <div><span>Arquivos</span><strong>{project.files?.length || 0} · {bytes(totalSize)}</strong></div>
        <div><span>Impressora</span><strong>{project.printer || 'A definir'}</strong></div>
        <div><span>Preenchimento</span><strong>{profile.infill || 'A definir'}</strong></div>
        <div><span>Estimativa</span><strong>{profile.estimatedTime || profile.estimatedWeight || 'Ainda não fatiado'}</strong></div>
      </div>
      <div className="workspace-actions">
        <button className="primary-action" onClick={onOpen}><FolderOpen size={18} /> Abrir projeto</button>
        <button className="secondary-action" onClick={() => onBambu(stl?.relativePath || '')}><MonitorUp size={18} /> Bambu Studio</button>
        {stl ? (
          <a className="secondary-action" href={stl.downloadUrl}><Download size={18} /> Baixar STL</a>
        ) : (
          <button className="secondary-action" disabled><Download size={18} /> Sem STL</button>
        )}
        <button className="secondary-action" onClick={onCustomize}><SlidersHorizontal size={18} /> Personalizar</button>
        <button className="icon-action" onClick={() => uploadRef.current?.click()} title="Adicionar arquivos"><Upload size={18} /></button>
        <input
          ref={uploadRef}
          type="file"
          multiple
          className="sr-only"
          accept=".stl,.step,.stp,.3mf,.glb,.gltf,.obj,.py,.scad,.png,.jpg,.jpeg,.webp,.pdf,.md,.txt"
          onChange={(event) => onUpload([...event.target.files])}
        />
      </div>
    </section>
  )
}

function ActivityPanel({ project }) {
  const history = project?.customizations || []
  return (
    <aside className="activity-panel">
      <div className="panel-label">Atividade recente</div>
      {history.length ? (
        <div className="activity-list">
          {history.slice(0, 6).map((item) => (
            <div className="activity-item" key={item.id}>
              <span className="activity-icon">{item.type === 'request' ? <Box size={17} /> : <WandSparkles size={17} />}</span>
              <div>
                <strong>{item.title}</strong>
                <p>{item.detail}</p>
                <time>{relativeDate(item.at)}</time>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="activity-empty"><History size={28} /><p>As customizações aparecerão aqui.</p></div>
      )}
      <div className="activity-foot">Histórico salvo no projeto local</div>
    </aside>
  )
}

function WorkflowStrip({ project, onRun }) {
  return (
    <section className="workflow-strip">
      <div className="panel-label">Fluxos rápidos</div>
      <div className="workflow-row">
        {workflows.map((workflow) => {
          const Icon = workflow.icon
          return (
            <button key={workflow.id} onClick={() => onRun(workflow)}>
              <Icon size={29} />
              <span><strong>{workflow.title}</strong><small>{workflow.description}</small></span>
              <ArrowRight size={18} />
            </button>
          )
        })}
      </div>
    </section>
  )
}

function LibraryView({ projects, selected, selectedId, onSelect, onNew, onCustomize, onRefresh, notify, settings, onMenu, onAskAgent }) {
  const [query, setQuery] = useState('')
  const [status, setStatus] = useState('')
  const [material, setMaterial] = useState('')
  const [printer, setPrinter] = useState('')
  const [busy, setBusy] = useState(false)

  const materials = [...new Set(projects.map((item) => item.material).filter(Boolean))]
  const printers = [...new Set(projects.map((item) => item.printer).filter(Boolean))]
  const filtered = projects.filter((project) => {
    const text = `${project.title} ${project.description} ${(project.tags || []).join(' ')}`.toLowerCase()
    return (!query || text.includes(query.toLowerCase()))
      && (!status || project.status === status)
      && (!material || project.material === material)
      && (!printer || project.printer === printer)
  })

  const updateStatus = async (nextStatus) => {
    setBusy(true)
    try {
      await api.updateProject(selected.id, { status: nextStatus })
      await onRefresh(selected.id)
      notify('Status atualizado')
    } catch (error) { notify(error.message, 'error') } finally { setBusy(false) }
  }

  const upload = async (files) => {
    if (!files.length) return
    setBusy(true)
    try {
      await uploadGrouped(selected.id, files)
      await onRefresh(selected.id)
      notify(`${files.length} ${files.length === 1 ? 'arquivo adicionado' : 'arquivos adicionados'}`)
    } catch (error) { notify(error.message, 'error') } finally { setBusy(false) }
  }

  const runWorkflow = async (workflow) => {
    if (workflow.action === 'new') return onNew(workflow.preset)
    if (workflow.action === 'customize') {
      if (!selected) return onNew()
      return onCustomize(selected)
    }
    const prompt = workflow.prompt(selected)
    onAskAgent(prompt, selected?.id)
  }

  return (
    <>
      <TopHeading title="Minha biblioteca" onNew={onNew} onMenu={onMenu} />
      <div className="filters-bar">
        <label className="search-field"><Search size={18} /><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar modelos" /></label>
        <label><select value={status} onChange={(e) => setStatus(e.target.value)}><option value="">Todos os status</option>{STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select><ChevronDown size={15} /></label>
        <label><select value={material} onChange={(e) => setMaterial(e.target.value)}><option value="">Todos os materiais</option>{materials.map((value) => <option key={value}>{value}</option>)}</select><ChevronDown size={15} /></label>
        <label><select value={printer} onChange={(e) => setPrinter(e.target.value)}><option value="">Todas as impressoras</option>{printers.map((value) => <option key={value}>{value}</option>)}</select><ChevronDown size={15} /></label>
      </div>
      {!projects.length ? (
        <>
          <EmptyLibrary onNew={onNew} />
          <WorkflowStrip project={null} onRun={runWorkflow} />
        </>
      ) : (
        <>
          {!filtered.length ? (
            <div className="no-results"><Search size={30} /><h2>Nenhum modelo encontrado</h2><p>Ajuste a busca ou remova os filtros.</p></div>
          ) : (
            <div className="library-grid">
              <ProjectList projects={filtered} selectedId={selectedId} onSelect={onSelect} />
              {selected && <ProjectWorkspace project={selected} onCustomize={() => onCustomize(selected)} onOpen={async () => { try { await api.openProject(selected.id); notify('Abertura enviada ao conector do Mac') } catch (error) { notify(error.message, 'error') } }} onBambu={async (relativePath) => { try { await api.openBambu(selected.id, relativePath); notify('Bambu Studio será aberto no Mac') } catch (error) { notify(error.message, 'error') } }} onUpload={upload} onStatusChange={updateStatus} busy={busy} />}
              {selected && <ActivityPanel project={selected} />}
            </div>
          )}
          <WorkflowStrip project={selected} onRun={runWorkflow} />
        </>
      )}
    </>
  )
}

function RequestsView({ projects, onNew, onSelect, onGoLibrary, onMenu }) {
  return (
    <>
      <TopHeading title="Solicitações" subtitle="Acompanhe o caminho de cada pedido até a impressão." onNew={onNew} onMenu={onMenu} />
      <section className="requests-table-wrap">
        <div className="requests-table-head"><span>Modelo</span><span>Status</span><span>Prioridade</span><span>Atualização</span><span>Arquivos</span><span /></div>
        {projects.length ? projects.map((project) => (
          <div className="requests-table-row" key={project.id}>
            <div className="request-title"><span className="request-box"><ProjectThumbnail project={project} /></span><span><strong>{project.title}</strong><small>{project.id}</small></span></div>
            <span className="request-status"><StatusDot status={project.status} /> {STATUS_LABEL[project.status]}</span>
            <span className={`priority priority-${project.priority}`}>{project.priority}</span>
            <span>{relativeDate(project.updatedAt)}</span>
            <span>{project.files?.length || 0}</span>
            <button onClick={() => { onSelect(project.id); onGoLibrary() }}>Abrir <ArrowRight size={15} /></button>
          </div>
        )) : <div className="table-empty"><FileText size={32} /><p>Nenhuma solicitação registrada.</p><button onClick={() => onNew()}>Criar pedido</button></div>}
      </section>
    </>
  )
}

function WorkflowsView({ selected, onNew, onCustomize, onMenu, onAskAgent }) {
  return (
    <>
      <TopHeading title="Fluxos rápidos" subtitle="Atalhos prontos para as tarefas mais comuns de impressão 3D." onNew={onNew} onMenu={onMenu} />
      <section className="workflow-page-intro">
        <Sparkles size={30} />
        <div><h2>Menos preparação, mais modelagem.</h2><p>Os atalhos acionam diretamente o Codex do Ateliê, com as skills, integrações e o modelo selecionado como contexto.</p></div>
      </section>
      <section className="skill-list">
        {skillShortcuts.map((item) => (
          <article key={item.title}>
            <div><span className="skill-icon"><Zap size={20} /></span><div><h3>{item.title}</h3><code>{item.skill}</code><p>{item.description}</p></div></div>
            <button onClick={() => onAskAgent(item.prompt, selected?.id)}><Bot size={17} /> Executar com Codex</button>
          </article>
        ))}
      </section>
      <section className="selected-context">
        <span>Contexto atual</span>
        {selected ? <><strong>{selected.title}</strong><code>{selected.id}</code><button onClick={() => onCustomize(selected)}>Registrar customização</button></> : <><strong>Nenhum modelo selecionado</strong><button onClick={() => onNew()}>Criar pedido</button></>}
      </section>
    </>
  )
}

const AGENT_STARTERS = [
  {
    title: 'Criar modelo funcional',
    detail: 'Modelar, validar e salvar STL/STEP/GLB no projeto.',
    prompt: 'Crie o modelo funcional descrito no projeto selecionado. Use a skill 3d-print-modeling, preserve fonte paramétrica, execute os gates aplicáveis e salve todos os artefatos e decisões no projeto.',
  },
  {
    title: 'Gerar com Meshy ou Tripo',
    detail: 'Escolher o provedor adequado e preparar para impressão.',
    prompt: 'Avalie o pedido e use Meshy AI ou Tripo AI somente se forem a melhor opção. Antes de qualquer geração paga, informe provedor, custo estimado e aguarde minha confirmação. Depois, baixe o formato adequado e prepare para impressão 3D.',
  },
  {
    title: 'Preparar para Bambu',
    detail: 'Analisar malha, orientar, fatiar e abrir no Bambu Studio.',
    prompt: 'Use as skills bambu-3mf-export e 3d-print-modeling para preparar o modelo selecionado para a Bambu Lab. Valide malha, escala, orientação, suporte e perfil. Gere 3MF e abra no Bambu Studio, mas não inicie a impressão física sem minha confirmação final.',
  },
]

function jobStatusLabel(status) {
  return ({ queued: 'Na fila', running: 'Executando', completed: 'Concluído', failed: 'Requer atenção' })[status] || status
}

function AgentView({ projects, selected, onSelect, onNew, onMenu, draft, clearDraft, notify }) {
  const [jobs, setJobs] = useState([])
  const [message, setMessage] = useState(draft?.message || '')
  const [projectId, setProjectId] = useState(draft?.projectId || selected?.id || '')
  const [sending, setSending] = useState(false)
  const [integration, setIntegration] = useState(null)

  const refresh = async () => {
    try {
      const [nextJobs, nextIntegration] = await Promise.all([api.listAgentJobs(20), api.getIntegrations()])
      setJobs(nextJobs)
      setIntegration(nextIntegration)
    } catch (error) {
      if (error.status !== 401) notify(error.message, 'error')
    }
  }

  useEffect(() => {
    if (draft?.message) {
      setMessage(draft.message)
      setProjectId(draft.projectId || selected?.id || '')
      clearDraft()
    }
  }, [draft])

  useEffect(() => {
    refresh()
    const timer = setInterval(refresh, 2200)
    return () => clearInterval(timer)
  }, [])

  const send = async (event) => {
    event?.preventDefault()
    if (!message.trim()) return
    setSending(true)
    try {
      await api.createAgentJob({ message, projectId: projectId || null, conversationId: 'principal' })
      setMessage('')
      await refresh()
      notify('Pedido enviado ao Codex do Ateliê')
    } catch (error) { notify(error.message, 'error') } finally { setSending(false) }
  }

  const online = integration?.bridge?.online
  return (
    <>
      <TopHeading title="Codex IA" subtitle="O mesmo agente deste projeto, agora dentro do Ateliê 3D." onNew={onNew} onMenu={onMenu} />
      <section className="agent-hero">
        <div className="agent-hero-mark"><BrainCircuit size={38} /></div>
        <div><span className={`connection-badge ${online ? 'connection-online' : ''}`}>{online ? 'Mac conectado' : 'Mac desconectado'}</span><h2>Descreva. O Codex executa.</h2><p>Modelagem, pesquisa, arquivos, skills, Build123d, Bambu Studio, Meshy e Tripo em um único fluxo auditável.</p></div>
        <div className="agent-capabilities"><span><ShieldCheck size={17} /> acesso privado</span><span><Cable size={17} /> integrações locais</span><span><History size={17} /> histórico persistente</span></div>
      </section>
      <section className="agent-layout">
        <div className="agent-main">
          <form className="agent-composer" onSubmit={send}>
            <div className="agent-context-row">
              <label>Contexto do projeto
                <select value={projectId} onChange={(event) => { setProjectId(event.target.value); onSelect(event.target.value || null) }}>
                  <option value="">Ateliê inteiro</option>
                  {projects.map((project) => <option key={project.id} value={project.id}>{project.title}</option>)}
                </select>
              </label>
              <span>{online ? 'Pronto para executar no Mac' : 'Os pedidos ficarão na fila até o conector voltar'}</span>
            </div>
            <textarea value={message} onChange={(event) => setMessage(event.target.value)} rows="5" placeholder="Ex.: crie um suporte articulado para meu iPhone, valide as folgas, gere o 3MF e abra no Bambu Studio…" />
            <div className="agent-submit-row"><span>Operações pagas e início de impressão sempre exigem confirmação.</span><button className="primary-action" disabled={sending || !message.trim()}><Send size={18} /> {sending ? 'Enviando…' : 'Executar com Codex'}</button></div>
          </form>
          <div className="agent-job-list">
            <div className="panel-label">Execuções recentes</div>
            {jobs.length ? jobs.map((job) => (
              <article className={`agent-job agent-job-${job.status}`} key={job.id}>
                <header><span><StatusDot status={job.status === 'completed' ? 'pronto' : job.status === 'failed' ? 'erro' : 'em-modelagem'} /><strong>{job.type === 'action' ? 'Ação local' : job.message}</strong></span><small>{jobStatusLabel(job.status)} · {relativeDate(job.updatedAt)}</small></header>
                {job.events?.length > 0 && <div className="agent-events">{job.events.slice(-8).map((item) => <div key={item.id}><span>{item.title}</span>{item.detail && <p>{item.detail}</p>}</div>)}</div>}
                {job.finalResponse && <pre className="agent-response">{job.finalResponse}</pre>}
                {job.error && <p className="agent-error">{job.error}</p>}
              </article>
            )) : <div className="agent-empty"><Bot size={31} /><p>Sua primeira execução aparecerá aqui.</p></div>}
          </div>
        </div>
        <aside className="agent-starters">
          <div className="panel-label">Começar rápido</div>
          {AGENT_STARTERS.map((starter) => <button key={starter.title} onClick={() => setMessage(starter.prompt)}><Sparkles size={20} /><span><strong>{starter.title}</strong><small>{starter.detail}</small></span><ArrowRight size={17} /></button>)}
        </aside>
      </section>
    </>
  )
}

function IntegrationCard({ title, subtitle, status, active, children, icon: Icon = Cable }) {
  return <article className="integration-card"><div className="integration-card-head"><span className="integration-icon"><Icon size={23} /></span><div><h3>{title}</h3><p>{subtitle}</p></div><span className={`integration-status ${active ? 'integration-active' : ''}`}>{status}</span></div><div className="integration-actions">{children}</div></article>
}

function IntegrationsView({ selected, onNew, onMenu, notify }) {
  const [state, setState] = useState(null)
  const load = async () => {
    try { setState(await api.getIntegrations()) } catch (error) { notify(error.message, 'error') }
  }
  useEffect(() => { load(); const timer = setInterval(load, 5000); return () => clearInterval(timer) }, [])
  const action = async (actionName, payload = {}, publicPayload = {}) => {
    try {
      await api.integrationAction({ action: actionName, projectId: selected?.id, payload, publicPayload })
      notify('Ação enviada ao conector do Mac')
    } catch (error) { notify(error.message, 'error') }
  }
  const cap = state?.capabilities || {}
  const online = state?.bridge?.online
  return (
    <>
      <TopHeading title="Integrações" subtitle="Aplicativos, APIs, MCPs e skills do seu fluxo 3D." onNew={onNew} onMenu={onMenu} />
      <section className="integration-overview"><div><Cable size={27} /><span><strong>{online ? 'Conector do Mac online' : 'Conector do Mac offline'}</strong><small>{online ? 'Aplicativos locais disponíveis para o Codex' : 'As tarefas ficam na fila e retomam quando o Mac conectar'}</small></span></div><span className={`connection-badge ${online ? 'connection-online' : ''}`}>{online ? 'ONLINE' : 'OFFLINE'}</span></section>
      <section className="integration-grid">
        <IntegrationCard icon={BrainCircuit} title="Codex" subtitle="Agente central com histórico, tools e skills." status={cap.codex?.authenticated ? 'Conectado' : 'Pendente'} active={cap.codex?.authenticated}><button disabled>{online ? 'Disponível no painel IA' : 'Aguardando Mac'}</button></IntegrationCard>
        <IntegrationCard icon={MonitorUp} title="Bambu Studio" subtitle="Abrir modelos, fatiar, validar 3MF e integrar AMS." status={cap.mcp?.bambuStudio ? 'MCP operacional' : cap.apps?.bambuStudio ? 'Instalado' : 'Indisponível'} active={cap.mcp?.bambuStudio}><button onClick={() => action('open-app', { app: 'bambu-studio' })} disabled={!online}>Abrir aplicativo</button>{selected && <button onClick={() => action('open-in-bambu')} disabled={!online}>Abrir modelo atual</button>}</IntegrationCard>
        <IntegrationCard icon={MonitorUp} title="Bambu Handy" subtitle="Aplicativo Bambu Lab disponível no Mac." status={cap.apps?.bambuHandy ? 'Instalado' : 'Indisponível'} active={cap.apps?.bambuHandy}><button onClick={() => action('open-app', { app: 'bambu-handy' })} disabled={!online || !cap.apps?.bambuHandy}>Abrir aplicativo</button></IntegrationCard>
        <IntegrationCard icon={Box} title="Build123d" subtitle="CAD paramétrico, medidas, auditoria e exportação." status={cap.mcp?.build123d ? 'MCP operacional' : 'Verificando'} active={cap.mcp?.build123d}><button onClick={() => notify('Use o Codex IA e peça a modelagem paramétrica com Build123d')}>Usar com Codex</button></IntegrationCard>
        <IntegrationCard icon={Sparkles} title="Meshy AI" subtitle="Texto/imagem para 3D e análise de impressão." status={cap.providers?.meshy ? 'API conectada' : 'Chave necessária'} active={cap.providers?.meshy}><button onClick={() => action('setup-provider', { provider: 'meshy' })} disabled={!online}>{cap.providers?.meshy ? 'Atualizar chave' : 'Conectar com segurança'}</button></IntegrationCard>
        <IntegrationCard icon={Sparkles} title="Tripo AI" subtitle="Texto, imagem e multiview para modelos 3D." status={cap.providers?.tripo ? 'API conectada' : 'Chave necessária'} active={cap.providers?.tripo}><button onClick={() => action('setup-provider', { provider: 'tripo' })} disabled={!online}>{cap.providers?.tripo ? 'Atualizar chave' : 'Conectar com segurança'}</button></IntegrationCard>
      </section>
      <section className="catalog-panel"><div><h2>Bibliotecas de modelos</h2><p>Acesso rápido a catálogos úteis; o Codex pode pesquisar, comparar licenças e importar referências.</p></div><div>{[['makerworld', 'MakerWorld'], ['printables', 'Printables'], ['thingiverse', 'Thingiverse']].map(([id, label]) => <button key={id} onClick={() => action('open-catalog', { catalog: id })} disabled={!online}>{label} <ExternalLink size={15} /></button>)}</div></section>
      <section className="skills-panel"><div className="panel-label">Skills ativas para o Ateliê</div><div>{(cap.skills || skillShortcuts.map((item) => item.skill)).map((skill) => <code key={skill}>{skill}</code>)}</div></section>
    </>
  )
}

function PairScreen({ onPaired }) {
  const [token, setToken] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const pair = async (event) => {
    event.preventDefault()
    setBusy(true); setError('')
    try { await api.pair(token); setToken(''); onPaired() } catch (reason) { setError(reason.message) } finally { setBusy(false) }
  }
  return <div className="pair-screen"><BrandMark /><section><ShieldCheck size={34} /><h1>Ateliê privado</h1><p>Este painel controla o Codex e os aplicativos do seu Mac. Faça o pareamento usando o código guardado no Chaves do macOS.</p><form onSubmit={pair}><input type="password" autoFocus value={token} onChange={(event) => setToken(event.target.value)} placeholder="Código de pareamento" /><button className="primary-action" disabled={busy || !token}>{busy ? 'Verificando…' : 'Parear dispositivo'}</button></form>{error && <span>{error}</span>}<small>No Mac principal, execute <code>npm run pair</code> na pasta do projeto.</small></section></div>
}

function SettingsView({ settings, onSaved, notify, onNew, onMenu }) {
  const [form, setForm] = useState(settings)
  const [saving, setSaving] = useState(false)
  useEffect(() => setForm(settings), [settings])
  const save = async (event) => {
    event.preventDefault()
    setSaving(true)
    try {
      const next = await api.updateSettings(form)
      onSaved(next)
      notify('Preferências salvas')
    } catch (error) { notify(error.message, 'error') } finally { setSaving(false) }
  }
  return (
    <>
      <TopHeading title="Configurações" subtitle="Padrões usados ao abrir um novo pedido." onNew={onNew} onMenu={onMenu} />
      <form className="settings-form" onSubmit={save}>
        <div className="settings-copy"><h2>Preferências do ateliê</h2><p>Esses dados ficam no ambiente privado do Ateliê e preenchem automaticamente os próximos pedidos.</p></div>
        <div className="settings-fields">
          <Field label="Nome do ambiente"><input value={form.atelierName || ''} onChange={(e) => setForm({ ...form, atelierName: e.target.value })} /></Field>
          <Field label="Impressora padrão"><input value={form.defaultPrinter || ''} onChange={(e) => setForm({ ...form, defaultPrinter: e.target.value })} /></Field>
          <Field label="Material padrão"><input value={form.defaultMaterial || ''} onChange={(e) => setForm({ ...form, defaultMaterial: e.target.value })} /></Field>
          <Field label="Bico padrão"><input value={form.defaultNozzle || ''} onChange={(e) => setForm({ ...form, defaultNozzle: e.target.value })} /></Field>
          <button className="primary-action" disabled={saving}><Check size={18} /> {saving ? 'Salvando…' : 'Salvar preferências'}</button>
        </div>
      </form>
      <section className="local-info"><FolderOpen size={22} /><div><strong>Armazenamento sincronizado</strong><p>Modelos e referências ficam no ambiente privado e são sincronizados com a pasta local <code>models/</code> quando o Codex trabalha neles.</p></div></section>
    </>
  )
}

function Field({ label, help, children, className = '' }) {
  return <label className={`field ${className}`}><span>{label}</span>{children}{help && <small>{help}</small>}</label>
}

function buildPrompt(form) {
  const dimensions = [form.dimensions.x, form.dimensions.y, form.dimensions.z].filter(Boolean)
  return `Objetivo
Criar um modelo 3D funcional e otimizado para impressão.

Resumo do pedido
• Nome: ${form.title || '—'}
• Descrição: ${form.description || '—'}
• Uso: ${form.useCase || '—'}
• Dimensões: ${dimensions.length ? `${dimensions.join(' × ')} ${form.dimensions.unit}` : 'não informadas'}
• Impressora: ${form.printer || '—'}
• Material: ${form.material || '—'}
• Prioridade: ${form.priority}
• Tags: ${form.tags || '—'}

Instruções para o Codex
Use a skill 3d-print-modeling, leia docs/DESIGN-RULES.md, preserve a fonte paramétrica e registre as decisões, versões e arquivos no projeto criado pelo Ateliê 3D.`
}

function NewRequestModal({ settings, preset, onClose, onCreated, notify }) {
  const [form, setForm] = useState({
    title: preset?.title || '',
    description: preset?.description || '',
    useCase: preset?.useCase || '',
    dimensions: { x: '', y: '', z: '', unit: 'mm' },
    printer: preset?.printer || settings.defaultPrinter || '',
    material: preset?.material || settings.defaultMaterial || '',
    priority: 'media',
    tags: (preset?.tags || []).join(', '),
  })
  const [files, setFiles] = useState([])
  const [saving, setSaving] = useState(false)
  const [startCodex, setStartCodex] = useState(true)
  const submit = async (event) => {
    event.preventDefault()
    setSaving(true)
    try {
      let project = await api.createProject({ ...form, tags: form.tags })
      if (files.length) project = await uploadGrouped(project.id, files) || project
      notify('Pedido criado e pasta local preparada')
      onCreated(project, startCodex)
    } catch (error) { notify(error.message, 'error') } finally { setSaving(false) }
  }
  return (
    <div className="modal-backdrop" role="presentation">
      <section className="request-modal" role="dialog" aria-modal="true" aria-labelledby="new-request-title">
        <div className="modal-heading"><div><h2 id="new-request-title">Novo pedido</h2><p>Você poderá ajustar medidas e anexar novos arquivos depois.</p></div><button onClick={onClose} aria-label="Fechar"><X /></button></div>
        <form onSubmit={submit}>
          <div className="request-form-column">
            <Field label="Nome do modelo" help="Nome curto e descritivo do modelo."><input autoFocus required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex.: Suporte para bobina" /></Field>
            <Field label="O que você precisa?" help="Inclua função, características importantes e restrições."><textarea required rows="4" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Descreva a peça e o resultado esperado." /></Field>
            <Field label="Dimensões conhecidas" help="Deixe em branco o que ainda não souber.">
              <div className="dimension-row">
                {['x', 'y', 'z'].map((axis, index) => <div key={axis}><input inputMode="decimal" aria-label={`Dimensão ${axis.toUpperCase()}`} value={form.dimensions[axis]} onChange={(e) => setForm({ ...form, dimensions: { ...form.dimensions, [axis]: e.target.value } })} placeholder={axis.toUpperCase()} />{index < 2 && <span>×</span>}</div>)}
                <select value={form.dimensions.unit} onChange={(e) => setForm({ ...form, dimensions: { ...form.dimensions, unit: e.target.value } })}><option>mm</option><option>cm</option><option>in</option></select>
              </div>
            </Field>
            <Field label="Uso da peça"><input value={form.useCase} onChange={(e) => setForm({ ...form, useCase: e.target.value })} placeholder="Ex.: suporte de bancada, reposição, gabarito" /></Field>
            <div className="two-fields">
              <Field label="Impressora"><input value={form.printer} onChange={(e) => setForm({ ...form, printer: e.target.value })} /></Field>
              <Field label="Material"><input value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })} /></Field>
            </div>
            <div className="two-fields">
              <Field label="Prioridade"><select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}><option value="baixa">Baixa</option><option value="media">Média</option><option value="alta">Alta</option></select></Field>
              <Field label="Tags"><input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="suporte, oficina, funcional" /></Field>
            </div>
            <label className="drop-zone">
              <input type="file" multiple onChange={(e) => setFiles([...e.target.files])} accept=".stl,.step,.stp,.3mf,.glb,.gltf,.obj,.py,.scad,.pdf,.png,.jpg,.jpeg,.webp,.md,.txt" />
              <Upload size={27} /><strong>Arquivos de referência</strong><span>{files.length ? `${files.length} ${files.length === 1 ? 'arquivo selecionado' : 'arquivos selecionados'}` : 'Arraste ou clique para selecionar'}</span>
            </label>
            <label className="codex-start-option"><input type="checkbox" checked={startCodex} onChange={(event) => setStartCodex(event.target.checked)} /><BrainCircuit size={19} /><span><strong>Iniciar com o Codex</strong><small>Analisar o pedido e começar a execução assim que a pasta estiver pronta.</small></span></label>
          </div>
          <div className="request-preview-column"><h3>Pedido para o Codex</h3><p>Prévia gerada a partir das informações fornecidas.</p><pre>{buildPrompt(form)}</pre></div>
          <div className="modal-actions"><span><FolderOpen size={17} /> Uma pasta local organizada será criada em <code>models/</code>.</span><div><button type="button" className="secondary-action" onClick={onClose}>Cancelar</button><button className="primary-action" disabled={saving}><FileText size={17} /> {saving ? 'Criando…' : 'Criar pedido'}</button></div></div>
        </form>
      </section>
    </div>
  )
}

function CustomizeModal({ project, onClose, onSaved, notify }) {
  const [title, setTitle] = useState('')
  const [detail, setDetail] = useState('')
  const [version, setVersion] = useState(project.version || '')
  const [files, setFiles] = useState([])
  const [saving, setSaving] = useState(false)
  const submit = async (event) => {
    event.preventDefault()
    setSaving(true)
    try {
      let updated = await api.addCustomization(project.id, { title, detail, version })
      if (files.length) updated = await uploadGrouped(project.id, files) || updated
      notify('Customização registrada')
      onSaved(updated)
    } catch (error) { notify(error.message, 'error') } finally { setSaving(false) }
  }
  return (
    <div className="modal-backdrop">
      <section className="custom-modal" role="dialog" aria-modal="true" aria-labelledby="custom-title">
        <div className="modal-heading"><div><h2 id="custom-title">Personalizar modelo</h2><p>{project.title}</p></div><button onClick={onClose} aria-label="Fechar"><X /></button></div>
        <form onSubmit={submit}>
          <Field label="Nome da alteração"><input autoFocus required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex.: Aumentar a base em 10 mm" /></Field>
          <Field label="Detalhes da customização" help="Inclua medidas, faces afetadas e o motivo da mudança."><textarea required rows="6" value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="Descreva exatamente o que deve mudar e o que deve permanecer igual." /></Field>
          <div className="two-fields"><Field label="Nova versão"><input value={version} onChange={(e) => setVersion(e.target.value)} /></Field><label className="compact-upload"><input type="file" multiple onChange={(e) => setFiles([...e.target.files])} /><Paperclip size={17} /> {files.length ? `${files.length} arquivo(s)` : 'Anexar referências'}</label></div>
          <div className="prompt-tip"><Clipboard size={19} /><div><strong>Pronto para o Codex</strong><p>A alteração será gravada no histórico do modelo e poderá ser usada como contexto no próximo pedido.</p></div></div>
          <div className="modal-actions"><span /><div><button type="button" className="secondary-action" onClick={onClose}>Cancelar</button><button className="primary-action" disabled={saving}>{saving ? 'Salvando…' : 'Registrar customização'}</button></div></div>
        </form>
      </section>
    </div>
  )
}

function Toast({ toast }) {
  if (!toast) return null
  return <div className={`toast toast-${toast.type}`}>{toast.type === 'error' ? <X size={17} /> : <Check size={17} />}{toast.message}</div>
}

export default function App() {
  const [projects, setProjects] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [view, setView] = useState('agent')
  const [settings, setSettings] = useState(DEFAULT_SETTINGS)
  const [newPreset, setNewPreset] = useState(null)
  const [newOpen, setNewOpen] = useState(false)
  const [customProject, setCustomProject] = useState(null)
  const [loading, setLoading] = useState(true)
  const [toast, setToast] = useState(null)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [authenticated, setAuthenticated] = useState(null)
  const [agentDraft, setAgentDraft] = useState(null)
  const toastTimer = useRef(null)

  const notify = (message, type = 'success') => {
    clearTimeout(toastTimer.current)
    setToast({ message, type })
    toastTimer.current = setTimeout(() => setToast(null), 3200)
  }

  const refresh = async (preferId) => {
    const next = await api.listProjects()
    setProjects(next)
    setSelectedId((current) => {
      if (preferId && next.some((item) => item.id === preferId)) return preferId
      if (current && next.some((item) => item.id === current)) return current
      return next[0]?.id || null
    })
    return next
  }

  const loadData = async () => {
    setLoading(true)
    return Promise.all([api.listProjects(), api.getSettings()])
      .then(([nextProjects, nextSettings]) => {
        setProjects(nextProjects)
        setSelectedId(nextProjects[0]?.id || null)
        setSettings(nextSettings)
        setAuthenticated(true)
      })
      .catch((error) => notify(error.message, 'error'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    const authenticate = async () => {
      try {
        let status = await api.authStatus()
        const match = window.location.hash.match(/^#pair=(.+)$/)
        if (!status.authenticated && match) {
          await api.pair(decodeURIComponent(match[1]))
          window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}`)
          status = await api.authStatus()
        }
        setAuthenticated(status.authenticated)
        if (status.authenticated) await loadData()
        else setLoading(false)
      } catch (error) {
        notify(error.message, 'error')
        setAuthenticated(false)
        setLoading(false)
      }
    }
    authenticate()
  }, [])

  const selected = useMemo(() => projects.find((item) => item.id === selectedId) || projects[0] || null, [projects, selectedId])

  const openNew = (preset = null) => { setNewPreset(preset); setNewOpen(true) }

  const askAgent = (message, projectId = null) => {
    setAgentDraft({ message, projectId: projectId || null, at: Date.now() })
    setView('agent')
  }

  if (loading || authenticated === null) return <div className="app-loading"><BrandMark /><span className="loading-line" /> <p>Conectando o seu Ateliê…</p></div>
  if (!authenticated) return <PairScreen onPaired={loadData} />

  return (
    <div className="app-shell">
      <Sidebar current={view} onChange={setView} settings={settings} projectCount={projects.length} open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      {sidebarOpen && <button className="sidebar-scrim" onClick={() => setSidebarOpen(false)} aria-label="Fechar menu" />}
      <main>
        {view === 'agent' && <AgentView projects={projects} selected={selected} onSelect={setSelectedId} onNew={openNew} onMenu={() => setSidebarOpen(true)} draft={agentDraft} clearDraft={() => setAgentDraft(null)} notify={notify} />}
        {view === 'library' && <LibraryView projects={projects} selected={selected} selectedId={selectedId} onSelect={setSelectedId} onNew={openNew} onCustomize={setCustomProject} onRefresh={refresh} notify={notify} settings={settings} onMenu={() => setSidebarOpen(true)} onAskAgent={askAgent} />}
        {view === 'requests' && <RequestsView projects={projects} onNew={openNew} onSelect={setSelectedId} onGoLibrary={() => setView('library')} onMenu={() => setSidebarOpen(true)} />}
        {view === 'workflows' && <WorkflowsView selected={selected} onNew={openNew} onCustomize={setCustomProject} onMenu={() => setSidebarOpen(true)} onAskAgent={askAgent} />}
        {view === 'integrations' && <IntegrationsView selected={selected} onNew={openNew} onMenu={() => setSidebarOpen(true)} notify={notify} />}
        {view === 'settings' && <SettingsView settings={settings} onSaved={setSettings} notify={notify} onNew={openNew} onMenu={() => setSidebarOpen(true)} />}
      </main>
      {newOpen && <NewRequestModal settings={settings} preset={newPreset} onClose={() => setNewOpen(false)} notify={notify} onCreated={async (project, startCodex) => { setNewOpen(false); await refresh(project.id); if (startCodex) { await api.createAgentJob({ message: 'Analise integralmente este novo pedido, aplique as skills de impressão 3D adequadas e comece a execução. Se faltar uma medida realmente crítica, explique exatamente qual é; caso contrário, avance e registre tudo no projeto.', projectId: project.id, conversationId: `projeto-${project.id}` }); setView('agent') } else setView('library') }} />}
      {customProject && <CustomizeModal project={customProject} onClose={() => setCustomProject(null)} notify={notify} onSaved={async (project) => { setCustomProject(null); await refresh(project.id) }} />}
      <Toast toast={toast} />
    </div>
  )
}
